import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribeToGoals, markCelebrationSeen, processGoalOutcomes } from '@shared/services/goalsService';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/RemoteConfigContext';
import { useDay } from '../context/DayContext';
import type { UserGoal } from '@shared/types';
import { logError } from '@/lib/log';

export interface GoalCelebration {
  goal: UserGoal;
  success: boolean;
}

/**
 * Manages the queue of goal celebrations that need to be shown.
 * Auto-detects completed/missed goals where celebrationSeen !== true.
 * Exposes the full queue — the modal handles paging internally.
 */
export const useGoalCelebrations = () => {
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const localDate = useDay();
  const [celebrations, setCelebrations] = useState<GoalCelebration[]>([]);
  const [frozenCelebrations, setFrozenCelebrations] = useState<GoalCelebration[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isReplay, setIsReplay] = useState(false);
  const replayCounter = useRef(0);

  // Evaluate expired goals on app load and day change
  useEffect(() => {
    if (!currentUser || !isFeatureEnabled('goals')) return;
    processGoalOutcomes(currentUser.uid).catch(error => {
      logError(error, { context: 'process_goal_outcomes' });
    });
  }, [currentUser, isFeatureEnabled, localDate]);

  // Subscribe to goals and detect unseen celebrations
  useEffect(() => {
    if (!currentUser || !isFeatureEnabled('goals')) return;

    const unsubscribe = subscribeToGoals(currentUser.uid, (goals) => {
      const unseen = goals
        .filter(g =>
          g.outcomeProcessed &&
          !g.celebrationSeen &&
          (g.status === 'completed' || g.status === 'missed')
        )
        .map(g => ({
          goal: g,
          success: g.status === 'completed',
        }));

      // Only update state when there are actual celebrations to show
      // (avoids re-render loop from setting a new empty array every snapshot)
      if (unseen.length > 0) {
        setCelebrations(unseen);
        // Freeze the list at open time so markSeen doesn't shrink it mid-session
        setFrozenCelebrations(prev => prev.length > 0 ? prev : unseen);
        setIsOpen(true);
      }
    });

    return () => unsubscribe();
  }, [currentUser, isFeatureEnabled]);

  // Mark a single celebration as seen
  const markSeen = useCallback(async (celebration: GoalCelebration) => {
    if (!currentUser || isReplay) return;

    try {
      await markCelebrationSeen(currentUser.uid, celebration.goal.id);
    } catch (error) {
      logError(error, { context: 'mark_celebration_seen' });
    }
  }, [currentUser, isReplay]);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsReplay(false);
    // Delay clearing so the modal's exit animation (400ms) can finish
    // before celebrations array becomes empty
    setTimeout(() => setFrozenCelebrations([]), 500);
  }, []);

  // Replay a specific goal's celebration (triggered from GoalCard)
  const replay = useCallback((goal: UserGoal) => {
    replayCounter.current += 1;
    setIsReplay(true);
    const replayCelebrations = [{
      goal,
      success: goal.status === 'completed',
    }];
    setCelebrations(replayCelebrations);
    setFrozenCelebrations(replayCelebrations);
    setIsOpen(true);
  }, []);

  return {
    celebrations: frozenCelebrations,
    isOpen,
    close,
    markSeen,
    replay,
    isReplay,
    replayKey: replayCounter.current,
  };
};
